// assets/AllCourses/Exercises/MatchingExercise.jsx

import React, { useEffect, useMemo, useState } from "react";
import useExerciseEngine from "@/hooks/useExerciseEngine";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";

import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function MatchingExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  isPreview = false,
  showResult = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE PAIRS ================= */
  const pairs = Array.isArray(exercise.matchingPairs)
    ? exercise.matchingPairs.map((p) => ({
        question: p?.question?.trim() || "",
        answer: p?.answer?.trim() || "",
        requireCapitalization: !!p?.requireCapitalization,
        type: "match", // 🔥 IMPORTANT for engine
      }))
    : [];

  /* ================= ENGINE ================= */
  const {
    answers,
    setAnswer,
    submitIfReady,
  } = useExerciseEngine({
    questions: pairs,
    showResult,
    onComplete,
    externalAnswers: userAnswer || {},
  });

  /* ================= LOCAL STATE ================= */
  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState(null);

  
  /* ================= SHUFFLE ================= */
  const shuffledAnswers = useMemo(() => {
    return pairs
      .map((p, idx) => ({
        answer: p.answer,
        originalIndex: idx,
      }))
      .sort(() => Math.random() - 0.5);
  }, [exercise._id]);

  /* ================= CLICK HANDLERS ================= */

  const handleQuestionClick = (qIdx) => {
    if (isEditMode || showResult) return;
    setSelectedQuestionIdx(qIdx);
  };

  const handleAnswerClick = (aIdx) => {
    if (selectedQuestionIdx === null || showResult) return;

    const answerValue = shuffledAnswers[aIdx]?.answer;

    // ✅ SAVE USING ENGINE
    setAnswer(selectedQuestionIdx, answerValue);

    // reset selection
    setSelectedQuestionIdx(null);
    submitIfReady();
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {pairs.map((p, idx) => (
          <Card key={idx} className="p-6">
            <div className="space-y-4">
              <StringField
                label={`Question ${idx + 1}`}
                value={p.question}
                onChange={(v) => {
                  const next = [...pairs];
                  next[idx].question = v;
                  onUpdate("matchingPairs", next);
                }}
              />

              <StringField
                label={`Answer ${idx + 1}`}
                value={p.answer}
                onChange={(v) => {
                  const next = [...pairs];
                  next[idx].answer = v;
                  onUpdate("matchingPairs", next);
                }}
              />

              <CapitalizationRule
                checked={p.requireCapitalization}
                onChange={(c) => {
                  const next = [...pairs];
                  next[idx].requireCapitalization = c;
                  onUpdate("matchingPairs", next);
                }}
              />

              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  onUpdate(
                    "matchingPairs",
                    pairs.filter((_, i) => i !== idx)
                  )
                }
              >
                Remove
              </Button>
            </div>
          </Card>
        ))}

        <Button
          variant="outline"
          onClick={() =>
            onUpdate("matchingPairs", [
              ...pairs,
              { question: "", answer: "", requireCapitalization: false },
            ])
          }
        >
          Add Pair
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!pairs.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No matching pairs available.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-4xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Match the Pairs</CardTitle>
        <CardDescription>
          Click question → then matching answer
        </CardDescription>
      </CardHeader>

      <CardContent className="grid md:grid-cols-2 gap-8">
        {/* QUESTIONS */}
        <div className="space-y-3">
          {pairs.map((p, qIdx) => {
            const selected = answers[qIdx];

            const isCorrect =
              selected &&
              selected.toLowerCase() === p.answer.toLowerCase();

            return (
              <Button
                key={qIdx}
                variant="outline"
                onClick={() => handleQuestionClick(qIdx)}
                className={cn(
                  "w-full justify-start text-left",

                  selectedQuestionIdx === qIdx && "bg-accent",

                  // ✅ USE ENGINE STYLE PATTERN
                  showResult &&
                    selected &&
                    isCorrect &&
                    "bg-green-100 border-green-400",

                  showResult &&
                    selected &&
                    !isCorrect &&
                    "bg-red-100 border-red-400"
                )}
              >
                {p.question}
              </Button>
            );
          })}
        </div>

        {/* ANSWERS */}
        <div className="space-y-3">
          {shuffledAnswers.map((item, aIdx) => {
            const used = Object.values(answers).includes(item.answer);

            return (
              <Button
                key={aIdx}
                variant="outline"
                disabled={used || showResult}
                onClick={() => handleAnswerClick(aIdx)}
                className={cn(
                  "w-full justify-start",
                  used && "opacity-50"
                )}
              >
                {item.answer}
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}